import { useState } from 'react'

import { ВОДА_И_СПИРТ_100_100, bulkMix, ru } from '../../lib/physics'
import { Choice, Predict } from './common'

/**
 * Смешиваем (урок 11): ведро картошки и ведро яблок — два ведра, картошки и гороха — меньше
 * двух: горох проходит в промежутки. Так же 100 мл воды и 100 мл спирта дают меньше 200 мл:
 * мелкие молекулы воды заходят в промежутки между крупными молекулами спирта.
 */

type Пара = 'картошка и яблоки' | 'картошка и горох' | 'вода и спирт'

interface Описание {
  крупное: string
  мелкое: string
  единица: string
  порция: number
  итог: number
  rМелкое: number // радиус мелкой частицы на рисунке, px
}

const ПАРЫ: Record<Пара, Описание> = {
  'картошка и яблоки': { крупное: 'картошка', мелкое: 'яблоки', единица: 'ведра', порция: 1, итог: bulkMix(1, 1, false), rМелкое: 12 },
  'картошка и горох': { крупное: 'картошка', мелкое: 'горох', единица: 'ведра', порция: 1, итог: bulkMix(1, 1, true), rМелкое: 3.5 },
  'вода и спирт': { крупное: 'спирт', мелкое: 'вода', единица: 'мл', порция: 100, итог: ВОДА_И_СПИРТ_100_100, rМелкое: 5 },
}

const R = 13 // крупная частица, px
const ШИРИНА = 112

/** Крупные — плотными рядами, мелкие — сначала в промежутки, остаток — слоем сверху. */
function сосуд(пара: Пара, смешано: boolean, x0: number) {
  const п = ПАРЫ[пара]
  const дно = 196
  const рядов = 3
  const крупные: [number, number][] = []
  for (let ряд = 0; ряд < рядов; ряд++)
    for (let i = 0; i < 4; i++) крупные.push([x0 + 1 + R + i * 2 * R + (ряд % 2 ? R / 2 : 0), дно - R - ряд * 1.8 * R])
  const мелкие: [number, number][] = []
  if (!смешано) return { крупные, мелкие, верх: дно - рядов * 1.8 * R }
  const влезает = п.rМелкое < R / 2
  if (влезает) {
    // промежутки между крупными
    for (let ряд = 0; ряд < рядов - 1; ряд++)
      for (let i = 0; i < 3; i++) мелкие.push([x0 + 1 + 2 * R + i * 2 * R + (ряд % 2 ? 0 : R / 2), дно - R - ряд * 1.8 * R - 0.9 * R])
  }
  const доля = (п.итог - п.порция) / п.порция // сколько объёма добавило мелкое
  const слой = доля * рядов * 1.8 * R
  const шаг = 2 * п.rМелкое + 1
  const верхКрупных = дно - рядов * 1.8 * R
  for (let y = верхКрупных - п.rМелкое; y > верхКрупных - слой; y -= шаг)
    for (let x = x0 + п.rМелкое + 2; x < x0 + ШИРИНА - п.rМелкое; x += шаг) мелкие.push([x, y])
  return { крупные, мелкие, верх: верхКрупных - слой }
}

export default function MixLab() {
  const [пара, setПара] = useState<Пара>('картошка и горох')
  const [смешано, setСмешано] = useState(false)
  const п = ПАРЫ[пара]
  const с = сосуд(пара, смешано, 124)
  const жидкость = пара === 'вода и спирт'

  const выбрать = (v: Пара) => {
    setПара(v)
    setСмешано(false)
  }

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 230" role="img" aria-label="Смешивание крупных и мелких частиц">
        <rect x={122} y={30} width={ШИРИНА + 4} height={168} rx={4} className="fill-paper ink" strokeWidth={1.5} />
        {жидкость && <rect x={124} y={с.верх} width={ШИРИНА} height={196 - с.верх} className="fill-cold" />}
        {с.крупные.map(([x, y], i) => (
          <circle key={`к${i}`} cx={x} cy={y} r={R - 1} className={жидкость ? 'fill-unit-a ink-muted' : 'fill-warm ink-muted'} />
        ))}
        {с.мелкие.map(([x, y], i) => (
          <circle key={`м${i}`} cx={x} cy={y} r={п.rМелкое} className={жидкость ? 'fill-el' : пара === 'картошка и горох' ? 'fill-unit-b' : 'fill-heavy'} />
        ))}
        <line x1={118} y1={с.верх} x2={250} y2={с.верх} className="ink-heavy" strokeDasharray="4 3" />
        <text x={254} y={с.верх + 4} className="lab-tick">{смешано ? `${ru(п.итог, п.порция === 1 ? 2 : 0)} ${п.единица}` : `${п.порция} ${п.единица === 'мл' ? 'мл' : 'ведро'}`}</text>
        <text x={180} y={218} textAnchor="middle" className="lab-label">
          {смешано ? `${п.крупное} + ${п.мелкое}` : `${п.крупное}; ${п.мелкое} — рядом, ${п.порция} ${п.единица === 'мл' ? 'мл' : 'ведро'}`}
        </text>
      </svg>

      <div className="lab-readout">
        <span>отдельно: {п.порция} + {п.порция} = <b>{2 * п.порция} {п.единица}</b></span>
        {смешано && <span>вместе: <b>{ru(п.итог, п.порция === 1 ? 2 : 0)} {п.единица}</b></span>}
      </div>

      <Choice options={(Object.keys(ПАРЫ) as Пара[]).map((v) => ({ value: v, label: v }))} value={пара} onChange={выбрать} />
      <button className="btn btn-primary" onClick={() => setСмешано(true)} disabled={смешано}>Смешать</button>

      <Predict
        question="100 мл воды и 100 мл спирта слили вместе. Сколько получится?"
        options={['больше 200 мл', 'ровно 200 мл', 'меньше 200 мл']}
        right={2}
        revealed={пара === 'вода и спирт' && смешано}
        explain="Около 193 мл. Молекулы воды мельче молекул спирта (H₂O против C₂H₅OH) и заходят в промежутки между ними — как горох между картофелинами. Значит, между молекулами есть промежутки."
      />
      <p className="muted small">
        Молекулы на рисунке — шарики условного размера. Важно одно: у частиц одного размера промежутков друг для друга
        нет, а мелкие проходят между крупными.
      </p>
    </div>
  )
}
